import * as _ from 'lodash';

import * as Types from '../model/types';
import * as OpKinds from '../opKinds';
import * as OpTypes from '../opTypes';
import * as Graph from '../graph';

// This function transforms bad JSON into usable JSON. In particular,
// JSON that uses the raw `Infinity` representation. Python's JSON
// serializer actually outputs the text `Infinity` which is invalid JSON.
// https://docs.python.org/3/library/json.html#standard-compliance-and-interoperability
const memoJSONParseSafe = _.memoize((text: string) => {
  const inf = 'Infinity';
  const infReplace = '___wb___Infinity___wb___';
  const ninf = '-Infinity';
  const ninfReplace = '___wb___-Infinity___wb___';
  let quoteCount = 0;

  for (let i = 0; i < text.length; i++) {
    if (
      text[i] === '"' &&
      (i === 0 || text[i - 1] !== '\\' || (i > 1 && text[i - 2] === '\\'))
    ) {
      quoteCount++;
    } else if (quoteCount % 2 === 0) {
      if (
        text[i] === inf[0] &&
        i + inf.length <= text.length &&
        text.substring(i, i + inf.length) === inf
      ) {
        text =
          text.slice(0, i) +
          '"' +
          infReplace +
          '"' +
          text.slice(i + inf.length, text.length);
        i += 1 + infReplace.length;
      } else if (
        text[i] === ninf[0] &&
        i + ninf.length <= text.length &&
        text.substring(i, i + ninf.length) === ninf
      ) {
        text =
          text.slice(0, i) +
          '"' +
          ninfReplace +
          '"' +
          text.slice(i + ninf.length, text.length);
        i += 1 + ninfReplace.length;
      }
    }
  }
  const res = JSON.parse(text, (key, val) => {
    if (val === infReplace) {
      return Infinity;
    } else if (val === ninfReplace) {
      return -Infinity;
    }
    return val;
  });

  return res;
});

const makeFileOp = OpKinds.makeStandardOp;

const fileArgTypes = {
  file: {type: 'file' as const},
};

const fileArgDescription = `A ${Graph.docType('file')}`;

export const opFileType = makeFileOp({
  hidden: true,
  name: 'file-type',
  argTypes: fileArgTypes,
  description: `Returns the type of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The type of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'type',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileMetadata(
      artifact.id,
      path
    );
    let typeResult: Types.Type = 'none';
    if (result != null) {
      if (result.type === 'dir') {
        typeResult = {type: 'dir' as const};
      } else {
        typeResult = Types.filePathToType(path);
      }
    }
    return typeResult;
  },
});

export const opFileDir = makeFileOp({
  hidden: true,
  name: 'file-dir',
  argTypes: fileArgTypes,
  description: `Returns the ${Graph.docType('dir')} of the ${Graph.docType(
    'file'
  )}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The ${Graph.docType('dir')} of the ${Graph.docType(
    'file'
  )}`,
  returnType: inputTypes => ({type: 'dir'}),
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    // const {artifactVersion} = inputs;
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileMetadata(
      artifact.id,
      path
    );
    if (result == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    if (result.type !== 'dir') {
      throw new Error('expected dir');
    }
    return result;
  },
});

export const opFilePath = makeFileOp({
  hidden: true,
  name: 'file-path',
  argTypes: fileArgTypes,
  description: `Returns the path of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The path of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'string',
  resolver: ({file}) => file.path,
});

export const opFileSize = makeFileOp({
  name: 'file-size',
  argTypes: fileArgTypes,
  description: `Returns the size of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The size of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'number',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileMetadata(
      artifact.id,
      path
    );
    if (result == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    if (result.type === 'dir') {
      throw new Error('expected file');
    }
    return result.size;
  },
});

export const opFileDigest = makeFileOp({
  name: 'file-digest',
  argTypes: fileArgTypes,
  description: `Returns the digest of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The digest of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'string',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileMetadata(
      artifact.id,
      path
    );
    if (result == null) {
      return undefined;
    }
    if (result.type === 'dir') {
      throw new Error('expected file');
    }
    return result.digest;
  },
});

export const opFileTable = OpKinds.makeTagConsumingStandardOp({
  name: 'file-table',
  argTypes: fileArgTypes,
  description: `Returns the contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('table')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('table')}`,
  returnType: inputTypes =>
    Types.taggedValue(
      Types.isTaggedValue(inputTypes.file)
        ? (inputTypes.file as Types.TaggedValueType).tag
        : null,
      Types.withFileTag(Types.maybe({type: 'table', columnTypes: {}}), {
        type: 'file',
        extension: 'json',
        wbObjectType: {type: 'table', columnTypes: {}},
      })
    ),
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context,
    engine
  ) => {
    // TODO: We need to invert the tag logic here as well to return
    // Tagged none (e.g.)
    let res: any;
    const fileVal = OpTypes.getValueFromTaggedValue(file);
    const fileTag = OpTypes.getTagFromTaggedValue(file);
    const {artifact, path} = fileVal;
    if (artifact == null) {
      const runTag = OpTypes.getActualNamedTagFromValue(file, 'run');
      const entityNameTag = OpTypes.getActualNamedTagFromValue(
        file,
        'entityName'
      );
      const projectNameTag = OpTypes.getActualNamedTagFromValue(
        file,
        'projectName'
      );
      if (
        runTag?.run?.name &&
        entityNameTag?.entityName &&
        projectNameTag?.projectName
      ) {
        const fileContents = await context.backend.getRunFileContents(
          projectNameTag.projectName,
          runTag.run.name,
          path,
          entityNameTag.entityName
        );
        if (fileContents.contents != null) {
          try {
            res = Types.concreteTaggedValue(
              {
                file: fileVal,
              },
              memoJSONParseSafe(fileContents.contents)
            );
          } catch (e) {
            console.warn('Error parsing table contents', fileVal);
          }
        }
      }
      return fileTag != null ? Types.concreteTaggedValue(fileTag, res) : res;
    }
    try {
      // This errors if the sequence has been deleted (or a race case in which
      // the artifact is not created before the history step referencing it comes in)
      const {refFileId, contents} =
        await context.backend.getArtifactFileContents(artifact.id, path);
      if (contents != null) {
        try {
          res = Types.concreteTaggedValue(
            {
              file:
                refFileId != null
                  ? {
                      artifact: {id: refFileId.artifactId},
                      path: refFileId.path,
                    }
                  : fileVal,
            },
            memoJSONParseSafe(contents)
          );
        } catch (e) {
          console.warn('Error parsing table contents', fileVal);
        }
      }
      return fileTag != null ? Types.concreteTaggedValue(fileTag, res) : res;
    } catch (e) {
      return null;
    }
  },
});

export const opFileJoinedTable = makeFileOp({
  name: 'file-joinedTable',
  argTypes: {
    file: Types.maybe({
      type: 'file',
      extension: 'json',
      wbObjectType: {type: 'joined-table', columnTypes: {}},
    }),
  },
  description: `Returns the contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('joined-table')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('joined-table')}`,
  returnType: inputTypes => ({type: 'joined-table', columnTypes: {}}),
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileContents(
      artifact.id,
      path
    );
    const contents = result.contents;
    if (contents == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    const joinedTable = memoJSONParseSafe(contents);
    return {artifact: {id: artifact.id}, path, joinedTable};
  },
});

export const opFilePartitionedTable = makeFileOp({
  name: 'file-partitionedTable',
  argTypes: {
    file: Types.maybe({
      type: 'file',
      extension: 'json',
      wbObjectType: {type: 'partitioned-table', columnTypes: {}},
    }),
  },
  description: `Returns the contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('partitioned-table')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The contents of the ${Graph.docType(
    'file'
  )} as a ${Graph.docType('partitioned-table')}`,
  returnType: inputTypes => ({type: 'partitioned-table', columnTypes: {}}),
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileContents(
      artifact.id,
      path
    );
    const contents = result.contents;
    if (contents == null) {
      return undefined;
    }
    const partitionedTable = memoJSONParseSafe(contents);
    return {artifact: {id: artifact.id}, path, partitionedTable};
  },
});

export const opFileContents = makeFileOp({
  name: 'file-contents',
  argTypes: fileArgTypes,
  description: `Returns the contents of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The contents of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'string',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileContents(
      artifact.id,
      path
    );
    const contents = result.contents;
    if (contents == null) {
      return undefined;
      // throw new Error('didnt load table contents (opArtifactsTable)');
    }
    return contents;
  },
});

// Don't expose this. These urls expire so they have to be used carefully.
export const opFileDirectUrl = makeFileOp({
  hidden: true,
  name: 'file-directUrl',
  argTypes: fileArgTypes,
  description: `Returns the direct url of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The direct url of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'string',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileDirectUrl(
      artifact.id,
      path
    );
    return result.directUrl;
  },
});

// Don't expose this. This op accepts an `asOf` parameter, which in practice
// can be used to ensure that we do not return cached results. Be careful when
// using this op - see PanelTrace.tsx/useDirectUrlNodeWithExpiration for an
// example.
export const opFileDirectUrlAsOf = makeFileOp({
  hidden: true,
  name: 'file-directUrlAsOf',
  argTypes: {...fileArgTypes, asOf: 'number'},
  description: `Returns the direct url of the ${Graph.docType(
    'file'
  )}. The \`asOf\` parameter can be used to ensure that we do not return cached results - modify the \'asOf\` parameter to force a new result.`,
  argDescriptions: {
    file: fileArgDescription,
    asOf: 'The value to use for caching',
  },
  returnValueDescription: `The direct url of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'string',
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const result = await context.backend.getArtifactFileDirectUrl(
      artifact.id,
      path
    );
    return result.directUrl;
  },
});

export const opFileMedia = makeFileOp({
  hidden: true,
  name: 'file-media',
  argTypes: fileArgTypes,
  description: `Returns a ${Graph.docType('file')} as its Media type`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The ${Graph.docType('file')} as its Media type`,
  returnType: ({file}) => {
    if (!Types.isFile(file)) {
      throw new Error('invalid');
    }
    const wbObjectType = file.wbObjectType;
    if (wbObjectType == null) {
      throw new Error('invalid');
    }
    return Types.withFileTag(wbObjectType, file);
  },
  resolver: async (
    {file},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const {artifact, path} = file;
    if (artifact == null) {
      // TODO: Add support for files stored on run without artifact
      return undefined;
    }
    const {contents} = await context.backend.getArtifactFileContents(
      artifact.id,
      path
    );
    if (contents == null) {
      return undefined;
    }
    return Types.concreteTaggedValue({file}, JSON.parse(contents));
  },
});

export const opFileArtifactVersion = makeFileOp({
  hidden: true,
  name: 'file-artifactVersion',
  argTypes: fileArgTypes,
  description: `Returns the ${Graph.docType(
    'artifactVersion'
  )} of the ${Graph.docType('file')}`,
  argDescriptions: {
    file: fileArgDescription,
  },
  returnValueDescription: `The ${Graph.docType(
    'artifactVersion'
  )} of the ${Graph.docType('file')}`,
  returnType: inputTypes => 'artifactVersion',
  resolver: ({file}) => {
    return file.artifact;
  },
});
